import React, { useMemo } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { apiPost, apiPut } from "../../utils/api";
import { useLanguage } from "../../contexts/LanguageContext";
import { useAuth } from "../../contexts/AuthContext";
import { AdminFormDialog, AdminField } from "./AdminFormDialog";
import { Input, Textarea, SelectNative } from "../ui/input";
import { DocumentUpload } from "../subcomponents/DocumentUpload";
import { ScheduleGridEditor } from "../schedules/ScheduleGridEditor";
import { ScheduleDisplayMode } from "../../types/schedule";
import {
  defaultGridData,
  getScheduleGrid,
  parseScheduleDescription,
  serializeGridDescription,
} from "../../utils/scheduleGrid";
import type { ScheduleGridData } from "../../types/schedule";

type ScheduleFormState = {
  type: "cours" | "examen";
  title: string;
  facultyId: string;
  filiereId: string;
  promotion: string;
  academicYear: string;
  semester: string;
  startDate: string;
  endDate: string;
  location: string;
  fileUrl: string;
  description: string;
  displayMode: ScheduleDisplayMode;
  gridData: ScheduleGridData;
};

const EMPTY_SCHEDULE = (): ScheduleFormState => ({
  type: "cours",
  title: "",
  facultyId: "",
  filiereId: "",
  promotion: "L1",
  academicYear: `${new Date().getFullYear()}-${new Date().getFullYear() + 1}`,
  semester: "S1",
  startDate: "",
  endDate: "",
  location: "",
  fileUrl: "",
  description: "",
  displayMode: "document",
  gridData: defaultGridData(),
});

type Props = {
  open: boolean;
  editing: any | null;
  faculties: any[];
  isAdmin: boolean;
  onClose: () => void;
  onSaved: () => void;
};

const FORM_ID = "schedule-form";

export const AdminScheduleModal: React.FC<Props> = ({ open, editing, faculties, isAdmin, onClose, onSaved }) => {
  const { t } = useLanguage();
  const { user } = useAuth();

  const schema = Yup.object({
    type: Yup.string().oneOf(["cours", "examen"]).required(),
    title: Yup.string().trim().required(t.common.required),
    promotion: Yup.string().required(t.common.required),
    academicYear: Yup.string().trim().required(t.common.required),
    facultyId: isAdmin ? Yup.string().required(t.admin.selectFaculty) : Yup.string(),
    filiereId: Yup.string(),
    semester: Yup.string(),
    startDate: Yup.string(),
    endDate: Yup.string(),
    location: Yup.string(),
    fileUrl: Yup.string(),
    description: Yup.string(),
    displayMode: Yup.string().oneOf(["grid", "document"]),
  });

  const initial: ScheduleFormState = useMemo(() => {
    if (!editing) {
      const fac = faculties.find((f) => String(f.id) === String(user?.facultyId));
      return {
        ...EMPTY_SCHEDULE(),
        facultyId: user?.facultyId ? String(user.facultyId) : "",
        gridData: defaultGridData(fac?.name || ""),
      };
    }
    const parsed = parseScheduleDescription(editing.description);
    const grid =
      getScheduleGrid(editing) ||
      parsed.grid ||
      defaultGridData(editing.faculty?.name || "");
    const mode: ScheduleDisplayMode =
      editing.displayMode === "grid" || editing.displayMode === "document"
        ? editing.displayMode
        : parsed.mode;
    return {
      type: editing.type,
      title: editing.title,
      facultyId: String(editing.facultyId || ""),
      filiereId: editing.filiereId ? String(editing.filiereId) : "",
      promotion: editing.promotion,
      academicYear: editing.academicYear || "",
      semester: editing.semester || "",
      startDate: editing.startDate ? editing.startDate.slice(0, 16) : "",
      endDate: editing.endDate ? editing.endDate.slice(0, 16) : "",
      location: editing.location || "",
      fileUrl: editing.fileUrl || "",
      description: parsed.notes || (mode === "document" ? editing.description || "" : ""),
      displayMode: mode,
      gridData: grid,
    };
  }, [editing, faculties, user?.facultyId]);

  const title = editing ? t.admin.editSchedule : t.admin.newSchedule;
  const submitLabel = editing ? t.admin.save : isAdmin ? t.admin.publish : t.admin.submitForReview;

  return (
    <Formik
      initialValues={initial}
      validationSchema={schema}
      enableReinitialize
      onSubmit={async (values, { setSubmitting }) => {
        const faculty = faculties.find((f) => String(f.id) === String(values.facultyId));
        const gridWithFaculty = {
          ...values.gridData,
          facultyName: values.gridData.facultyName || faculty?.name || "",
        };
        const description =
          values.displayMode === "grid"
            ? serializeGridDescription(gridWithFaculty, values.description)
            : values.description;

        const payload = {
          type: values.type,
          title: values.title.trim(),
          facultyId: values.facultyId ? Number(values.facultyId) : undefined,
          filiereId: values.filiereId ? Number(values.filiereId) : null,
          promotion: values.promotion,
          academicYear: values.academicYear,
          semester: values.semester,
          startDate: values.startDate || null,
          endDate: values.endDate || null,
          location: values.location,
          fileUrl: values.fileUrl,
          description,
          displayMode: values.displayMode,
          gridData: values.displayMode === "grid" ? gridWithFaculty : null,
        };
        try {
          if (editing) {
            await apiPut(`/schedules/${editing.id}`, payload);
            toast.success(t.common.saveSuccess);
          } else {
            await apiPost("/schedules", payload);
            toast.success(isAdmin ? t.admin.published : t.admin.submitted);
          }
          onSaved();
          onClose();
        } catch (e: any) {
          toast.error(e?.response?.data?.reason || e?.messageText || t.common.error);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ values, errors, touched, isSubmitting, setFieldValue }) => {
        const filieres =
          faculties.find((f) => String(f.id) === String(values.facultyId))?.filieres || [];

        return (
          <AdminFormDialog
            open={open}
            onClose={onClose}
            title={title}
            subtitle={t.admin.scheduleSubtitle}
            testId="schedule-modal"
            className="max-w-4xl"
            formId={FORM_ID}
            submitLabel={submitLabel}
            isSubmitting={isSubmitting}
          >
            <Form id={FORM_ID} className="grid grid-cols-2 gap-4 pb-4">
              <AdminField label={t.admin.scheduleType}>
                <Field as={SelectNative} name="type" data-testid="schedule-type">
                  <option value="cours">{t.admin.scheduleCourses}</option>
                  <option value="examen">{t.admin.scheduleExams}</option>
                </Field>
              </AdminField>
              <AdminField label={t.admin.promotion} required error={touched.promotion ? errors.promotion : undefined}>
                <Field as={SelectNative} name="promotion" data-testid="schedule-promotion">
                  {["L0", "L1", "L2", "L3", "M1", "M2", "L3 GE", "L3 GCMI", "M1 GC SOA", "M2 GC SOA", "M1 GC HCH"].map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </Field>
              </AdminField>
              <div className="col-span-2">
                <AdminField label={t.admin.contentTitle} required error={touched.title ? errors.title : undefined}>
                  <Field as={Input} name="title" data-testid="schedule-title" />
                </AdminField>
              </div>
              {isAdmin && (
                <AdminField label={t.admin.faculty} required error={touched.facultyId ? errors.facultyId : undefined}>
                  <Field
                    as={SelectNative}
                    name="facultyId"
                    data-testid="schedule-faculty"
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      setFieldValue("facultyId", e.target.value);
                      setFieldValue("filiereId", "");
                      const fac = faculties.find((f) => String(f.id) === e.target.value);
                      if (fac?.name) setFieldValue("gridData.facultyName", fac.name);
                    }}
                  >
                    <option value="">{t.admin.selectFaculty}</option>
                    {faculties.map((f) => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </Field>
                </AdminField>
              )}
              <AdminField label={t.admin.programOptional}>
                <Field as={SelectNative} name="filiereId">
                  <option value="">{t.admin.allPrograms}</option>
                  {filieres.map((fi: any) => (
                    <option key={fi.id} value={fi.id}>{fi.name}</option>
                  ))}
                </Field>
              </AdminField>
              <AdminField label={t.admin.academicYear} required error={touched.academicYear ? errors.academicYear : undefined}>
                <Field as={Input} name="academicYear" placeholder="2025-2026" />
              </AdminField>
              <AdminField label={t.admin.semester}>
                <Field as={SelectNative} name="semester">
                  <option value="S1">S1</option>
                  <option value="S2">S2</option>
                  <option value="">—</option>
                </Field>
              </AdminField>
              <AdminField label={t.admin.startDate}>
                <Field as={Input} name="startDate" type="datetime-local" />
              </AdminField>
              <AdminField label={t.admin.endDate}>
                <Field as={Input} name="endDate" type="datetime-local" />
              </AdminField>
              <div className="col-span-2">
                <AdminField label={t.admin.location}>
                  <Field as={Input} name="location" />
                </AdminField>
              </div>

              <div className="col-span-2">
                <AdminField label={t.admin.scheduleDisplayMode}>
                  <Field
                    as={SelectNative}
                    name="displayMode"
                    data-testid="schedule-display-mode"
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      const mode = e.target.value as ScheduleDisplayMode;
                      setFieldValue("displayMode", mode);
                    }}
                  >
                    <option value="grid">{t.admin.scheduleModeGrid}</option>
                    <option value="document">{t.admin.scheduleModeDocument}</option>
                  </Field>
                </AdminField>
              </div>

              {values.displayMode === "grid" ? (
                <div className="col-span-2">
                  <ScheduleGridEditor
                    value={values.gridData}
                    onChange={(data) => setFieldValue("gridData", data)}
                  />
                </div>
              ) : null}

              <div className="col-span-2">
                <DocumentUpload
                  value={values.fileUrl}
                  onChange={(url) => setFieldValue("fileUrl", url)}
                  folder="ulpgl/uploads"
                  label={
                    values.displayMode === "grid"
                      ? t.admin.scheduleFileOptional
                      : t.admin.scheduleFile
                  }
                  testId="schedule-file"
                />
              </div>

              <div className="col-span-2">
                <AdminField label={values.displayMode === "grid" ? t.admin.scheduleNotes : t.admin.description}>
                  <Field as={Textarea} name="description" rows={3} placeholder={t.admin.scheduleNotesPlaceholder} />
                </AdminField>
              </div>
            </Form>
          </AdminFormDialog>
        );
      }}
    </Formik>
  );
};
